import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Truck, X } from 'lucide-react';
import { useState } from 'react';
import Card from '../../components/ui/Card';
import KpiTile from '../../components/ui/KpiTile';
import PageHeader from '../../components/ui/PageHeader';
import { adminApi, type AdminMachine } from '../../services/adminApi';

const AdminMachinesView = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AdminMachine | null>(null);

  const q = useQuery({
    queryKey: ['admin', 'machines'],
    queryFn: async () => (await adminApi.listMachines()).data,
  });

  const machines = q.data ?? [];
  const composters = machines.filter((m) => m.machineType === 'composter').length;
  const lifters = machines.filter((m) => m.machineType === 'lifter').length;
  const subscribers = machines.reduce((acc, m) => acc + m.subscriberCount, 0);

  return (
    <>
      <PageHeader
        title="Machines"
        subtitle="Staff-only · edit machine configuration and dimensions."
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <KpiTile label="Total machines" value={String(machines.length)} Icon={Truck} />
        <KpiTile label="Composters" value={String(composters)} />
        <KpiTile label="Lifters" value={String(lifters)} />
        <KpiTile label="Total subscribers" value={String(subscribers)} />
      </section>

      <Card className="mt-6" title={`Machines · ${machines.length}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-4 font-medium">ID</th>
                <th className="py-2 pr-4 font-medium">Type</th>
                <th className="py-2 pr-4 font-medium">Location</th>
                <th className="py-2 pr-4 font-medium">Capacity</th>
                <th className="py-2 pr-4 font-medium">Heat pads</th>
                <th className="py-2 pr-4 font-medium">Clean cam</th>
                <th className="py-2 pr-4 font-medium">Subscribers</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {machines.map((m) => (
                <tr key={m.uid} className="hover:bg-slate-50">
                  <td className="py-2.5 pr-4 font-mono text-xs font-medium text-slate-800">{m.id}</td>
                  <td className="py-2.5 pr-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${
                        m.machineType === 'composter'
                          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                          : 'bg-sky-50 text-sky-700 ring-sky-200'
                      }`}
                    >
                      {m.machineType}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-slate-500">{m.locationName ?? '—'}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs">
                    {m.weightCapacityKG != null ? `${m.weightCapacityKG} kg` : '—'}
                  </td>
                  <td className="py-2.5 pr-4">
                    <Bool value={m.heatpads} />
                  </td>
                  <td className="py-2.5 pr-4">
                    <Bool value={m.cleanCamera} />
                  </td>
                  <td className="py-2.5 pr-4 font-mono text-xs">{m.subscriberCount}</td>
                  <td className="py-2.5 pr-4">
                    <button
                      type="button"
                      onClick={() => setEditing(m)}
                      className="text-xs font-medium text-tfs-primary hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {machines.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-xs text-slate-400">
                    {q.isLoading ? 'Loading…' : 'No machines.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <EditMachineDialog
          machine={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['admin', 'machines'] });
            void qc.invalidateQueries({ queryKey: ['machines'] });
            setEditing(null);
          }}
        />
      )}
    </>
  );
};

const Bool = ({ value }: { value: boolean }) =>
  value ? <Check className="size-4 text-emerald-600" /> : <X className="size-4 text-slate-300" />;

interface EditProps {
  machine: AdminMachine;
  onClose: () => void;
  onSaved: () => void;
}

const EditMachineDialog = ({ machine, onClose, onSaved }: EditProps) => {
  const [machineType, setMachineType] = useState<'composter' | 'lifter'>(
    machine.machineType === 'lifter' ? 'lifter' : 'composter',
  );
  const [weightCapacityKG, setCapacity] = useState<string>(
    machine.weightCapacityKG?.toString() ?? '',
  );
  const [bottomVessel, setBottomVessel] = useState<string>(
    machine.bottomVesselToShaftCM?.toString() ?? '',
  );
  const [shaftToProbe, setShaftToProbe] = useState<string>(
    machine.shaftToProbeTopCM?.toString() ?? '',
  );
  const [heatpads, setHeatpads] = useState(machine.heatpads);
  const [cleanCamera, setCleanCamera] = useState(machine.cleanCamera);

  const toNum = (s: string): number | null => (s.trim() === '' ? null : Number(s));

  const save = useMutation({
    mutationFn: () =>
      adminApi.updateMachine(machine.uid, {
        machineType,
        weightCapacityKG: toNum(weightCapacityKG),
        bottomVesselToShaftCM: toNum(bottomVessel),
        shaftToProbeTopCM: toNum(shaftToProbe),
        heatpads,
        cleanCamera,
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Edit machine</h2>
            <p className="text-xs text-slate-500">
              {machine.id} · uid {machine.uid}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
              Type
            </label>
            <select
              value={machineType}
              onChange={(e) => setMachineType(e.target.value as 'composter' | 'lifter')}
              className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
            >
              <option value="composter">composter</option>
              <option value="lifter">lifter</option>
            </select>
          </div>

          <NumField label="Weight capacity (kg)" value={weightCapacityKG} onChange={setCapacity} />
          <NumField
            label="Bottom vessel → shaft (cm)"
            value={bottomVessel}
            onChange={setBottomVessel}
          />
          <NumField
            label="Shaft → probe top (cm)"
            value={shaftToProbe}
            onChange={setShaftToProbe}
          />

          <div className="grid grid-cols-2 gap-3">
            <Toggle label="Heat pads" value={heatpads} onChange={setHeatpads} />
            <Toggle label="Clean camera" value={cleanCamera} onChange={setCleanCamera} />
          </div>

          {save.isError && (
            <p className="text-xs text-red-600">
              Save failed: {(save.error as Error)?.message ?? 'unknown error'}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="rounded-md bg-tfs-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
};

const NumField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div>
    <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">{label}</label>
    <input
      type="number"
      step="any"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="—"
      className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 font-mono text-sm"
    />
  </div>
);

const Toggle = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) => (
  <label className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5 text-sm">
    <span className="text-slate-700">{label}</span>
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="size-4" />
  </label>
);

export default AdminMachinesView;
